import { observer } from "mobx-react"
import React, { useState } from "react"
import TripDestinationList from "../lists/TripDestinationList"
import Country from "../models/Country"
import Region from "../models/Region"
import TripDestination from "../models/TripDestination"
import webify from "../util/webify"
import Label from "./Label"
import SVG from "react-inlinesvg"
import mapPinSvg from "../../../priv/static/svg/generic/map-pin.svg"
import plusSVG from "../../../priv/static/svg/generic/plus.svg"
import xSVG from "../../../priv/static/svg/generic/x.svg"

interface TripDestinationFieldsProps {
  allCountries: any[]
  allRegions: any[]
  tripDestinations: any[]
  showErrors: boolean
  permissionToEdit: boolean
}

const TripDestinationFields = observer((props: TripDestinationFieldsProps) => {
  const allCountries = props.allCountries.map((countryProps) => new Country(countryProps))
  const allRegions = props.allRegions.map((regionProps) => new Region(regionProps))
  const permissionToEdit = props.permissionToEdit
  const [showErrors] = useState(props.showErrors)
  const [tripDestinations] = useState(new TripDestinationList(props.tripDestinations))

  return (
    <div className="trip-destination-fields-component form-grid-setup lg:grid-cols-7">
      {tripDestinations.nonDeleted.map((tripDestination, idx) => (
        <>
          <TripDestinationInputs
            allCountries={allCountries}
            allRegions={allRegions}
            tripDestination={tripDestination}
            showErrors={showErrors}
            index={idx}
            isLast={tripDestinations.isLast(tripDestination)}
          />
          {permissionToEdit && (
            <AddRemoveDestinations
              tripDestination={tripDestination}
              isFirst={tripDestinations.isFirst(tripDestination)}
              isLast={tripDestinations.isLast(tripDestination)}
              isSingle={tripDestinations.hasSingleDestination()}
              add={() => tripDestinations.addDestination()}
              remove={() => (tripDestination.delete = true)}
            />
          )}
        </>
      ))}
    </div>
  )
})

interface TripDestinationInputsProps {
  tripDestination: TripDestination
  index: number
  isLast: boolean
  allCountries: Country[]
  allRegions: Region[]
  showErrors: boolean
}

const TripDestinationInputs = observer((props: TripDestinationInputsProps) => {
  const { allCountries, allRegions, tripDestination, index, isLast, showErrors } = props

  let baseInputName = `trip[trip_destinations][${index}]`
  let country = allCountries.find((country) => country.id === tripDestination.countryId)
  let region = allRegions.find((region) => region.id === tripDestination.regionId)

  let cityError, regionError

  if (showErrors && tripDestination.city.length === 0) {
    cityError = "can't be blank"
  }

  if (!tripDestination.delete) {
    return (
      <>
        {tripDestination.id && <input type="hidden" name={baseInputName + "[id]"} value={tripDestination.id.toString()} />}
        <input type="hidden" name={baseInputName + "[order]"} value={index} />

        <div className="col-span-2">
          <Label htmlFor={baseInputName + "[city]"} label="Destination City" required error={cityError}></Label>
          <input
            type="text"
            name={baseInputName + "[city]"}
            data-role={isLast ? "destination-city-input" : ""}
            value={tripDestination.city}
            onChange={(e) => {
              tripDestination.city = e.target.value
            }}></input>
        </div>

        <div className="col-span-2">
          <Label htmlFor={baseInputName + "[country_id]"} label="Country" required></Label>
          <select
            name={baseInputName + "[country_id]"}
            value={country?.id}
            onChange={(e) => {
              const countryId = parseInt(e.target.value)
              tripDestination.countryId = countryId
            }}
            data-role={isLast ? "destination-country-input" : ""}>
            {allCountries.map((country) => (
              <option value={country.id}>{country.name}</option>
            ))}
          </select>
        </div>

        <div className="">
          {country?.id == Country.unitedStates().id && (
            <>
              <Label htmlFor={baseInputName + "[region_id]"} label="State" required></Label>
              <select
                name={baseInputName + "[region_id]"}
                value={region?.id}
                onChange={(e) => {
                  const regionId = parseInt(e.target.value)
                  tripDestination.regionId = regionId
                }}
                data-role={isLast ? "destination-region-input" : ""}>
                {allRegions.map((region) => (
                  <option value={region.id}>{region.code}</option>
                ))}
              </select>
            </>
          )}

          {country?.id !== Country.unitedStates().id && (
            <>
              <input type="hidden" name={baseInputName + "[region_id]"} value={""} />
            </>
          )}
        </div>
      </>
    )
  }
})

interface AddRemoveDestinationsProps {
  tripDestination: TripDestination
  isFirst: boolean
  isLast: boolean
  isSingle: boolean
  add: () => any
  remove: () => any
}

const AddRemoveDestinations = observer((props: AddRemoveDestinationsProps) => {
  const { tripDestination, isFirst, isLast, isSingle, add, remove } = props
  return (
    <div className="text-gray-700 flex items-center pt-5 text-sm col-span-2">
      {isSingle ? (
        <>
          <SVG src={mapPinSvg} className="mr-1" />
          <p>
            Multiple city tour?
            <br />
            <a className="font-bold inline-flex items-center" data-role="add-destination-link" onClick={add}>
              <SVG src={plusSVG} className="inline w-4 h-4 -mt-2px stroke-3" /> Add secondary cities
            </a>
          </p>
        </>
      ) : (
        <>
          <SVG src={mapPinSvg} className="mr-1" />
          <p>
            {!isSingle && (
              <>
                <a className="font-bold inline-flex items-center" onClick={remove}>
                  <SVG src={xSVG} className="inline w-4 h-4 stroke-3" /> Remove Destination
                </a>
                <br />
              </>
            )}
            {isLast && (
              <a className="font-bold" onClick={add}>
                <SVG src={plusSVG} className="inline w-4 h-4 -mt-2px -mr-3px stroke-3" /> Add Another Destination
              </a>
            )}
          </p>
        </>
      )}
    </div>
  )
})

export default TripDestinationFields
webify("trip-destination-fields", TripDestinationFields)
